"use client";

import { useLocale } from "@/context/LocaleContext";
import { I18nData } from "@/types/i18n";
import { useNewVersion } from "./use-new-version";

// Keep in sync with "en" in apps/api/bin/migrations/0005_seed_new_version_banner_i18n.py.
const FALLBACKS = {
  new_version_available:
    "A new version of the search is available. Your search and filters will be kept.",
  new_version_refresh: "Refresh",
  new_version_dismiss: "Dismiss",
};

const BUTTON_CLASSES =
  "inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded border-0 text-base focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-scifree-deep-ocean-blue";

interface Props {
  i18nData?: I18nData;
}

export default function NewVersionBanner({ i18nData }: Props) {
  const { locale } = useLocale();
  const { showNotice, dismissNotice } = useNewVersion();
  const texts = i18nData?.[locale];

  const dismiss = () => {
    document.getElementById("logoLink")?.focus();
    dismissNotice();
  };

  return (
    <div
      id="newVersionBanner"
      role="status"
      className={
        showNotice
          ? "box-border flex w-full flex-wrap items-center gap-x-4 gap-y-2 bg-scifree-greyish-blue px-4 py-3 text-scifree-deep-ocean-blue"
          : undefined
      }
    >
      {showNotice && (
        <>
          <p className="m-0 min-w-0 flex-1 basis-60 text-base leading-normal">
            {texts?.new_version_available ?? FALLBACKS.new_version_available}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className={`${BUTTON_CLASSES} bg-scifree-teal px-4 font-bold text-white hover:bg-scifree-deep-ocean-blue`}
            >
              {texts?.new_version_refresh ?? FALLBACKS.new_version_refresh}
            </button>
            <button
              type="button"
              onClick={dismiss}
              aria-label={texts?.new_version_dismiss ?? FALLBACKS.new_version_dismiss}
              className={`${BUTTON_CLASSES} bg-transparent text-scifree-deep-ocean-blue hover:bg-scifree-deep-ocean-blue hover:text-white`}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M6 6L18 18M18 6L6 18" />
              </svg>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
